import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Head } from "@inertiajs/react"
import { IconArrowLeft, IconArrowRight, IconCheck } from "@tabler/icons-react"

export interface Section {
  title: string
  content: string | string[]
  icon?: string
  color?: string
}

export interface EvolutionItem {
  title: string
  points: string[]
}

export interface LevelConfig {
  name: string
  level: number
  subtitle?: string
  emoji: string
  color: string
  description: string
  sections: Section[]
  observableBehaviors?: string[]
  deepDive?: string[]
  evolution?: {
    title: string
    items: EvolutionItem[]
  }
  prev?: { name: string; url: string }
  next?: { name: string; url: string }
  comingSoon?: boolean
}

const COLOR_MAP: Record<string, { bg: string; text: string; border: string; cardBg: string; dashed: string }> = {
  green: {
    bg: "bg-green-100 dark:bg-green-900",
    text: "text-green-600 dark:text-green-400",
    border: "border-green-200 dark:border-green-800",
    cardBg: "bg-green-50 dark:bg-green-900/20",
    dashed: "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20",
  },
  blue: {
    bg: "bg-blue-100 dark:bg-blue-900",
    text: "text-blue-600 dark:text-blue-400",
    border: "border-blue-200 dark:border-blue-800",
    cardBg: "bg-blue-50 dark:bg-blue-900/20",
    dashed: "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20",
  },
  yellow: {
    bg: "bg-yellow-100 dark:bg-yellow-900",
    text: "text-yellow-600 dark:text-yellow-400",
    border: "border-yellow-200 dark:border-yellow-800",
    cardBg: "bg-yellow-50 dark:bg-yellow-900/20",
    dashed: "border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20",
  },
  purple: {
    bg: "bg-purple-100 dark:bg-purple-900",
    text: "text-purple-600 dark:text-purple-400",
    border: "border-purple-200 dark:border-purple-800",
    cardBg: "bg-purple-50 dark:bg-purple-900/20",
    dashed: "border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20",
  },
  red: {
    bg: "bg-red-100 dark:bg-red-900",
    text: "text-red-600 dark:text-red-400",
    border: "border-red-200 dark:border-red-800",
    cardBg: "bg-red-50 dark:bg-red-900/20",
    dashed: "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20",
  },
  indigo: {
    bg: "bg-indigo-100 dark:bg-indigo-900",
    text: "text-indigo-600 dark:text-indigo-400",
    border: "border-indigo-200 dark:border-indigo-800",
    cardBg: "bg-indigo-50 dark:bg-indigo-900/20",
    dashed: "border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900/20",
  },
}

const SECTION_COLORS: Record<string, { bg: string; text: string }> = {
  blue: { bg: "bg-blue-100 dark:bg-blue-900", text: "text-blue-600 dark:text-blue-400" },
  green: { bg: "bg-green-100 dark:bg-green-900", text: "text-green-600 dark:text-green-400" },
  yellow: { bg: "bg-yellow-100 dark:bg-yellow-900", text: "text-yellow-600 dark:text-yellow-400" },
  purple: { bg: "bg-purple-100 dark:bg-purple-900", text: "text-purple-600 dark:text-purple-400" },
}

export function CareerPathwayLevel({ config }: { config: LevelConfig }) {
  const colors = COLOR_MAP[config.color]

  return (
    <>
      <Head title={`Career Pathway - ${config.name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>Growth & Career</BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/growth/career-pathway">Career Pathway</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{config.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{config.emoji}</span>
            <div>
              <h1 className="text-xl font-bold">{config.name}</h1>
              <span className="text-muted-foreground text-sm">
                Nivel {config.level}
                {config.subtitle ? ` \u2014 ${config.subtitle}` : ""}
              </span>
            </div>
          </div>
          {!config.comingSoon && <span className="text-muted-foreground text-xs">Last Updated: Feb 14, 2025</span>}
        </div>

        {config.comingSoon ? (
          <div
            className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 ${colors.dashed}`}
          >
            <span className="mb-4 text-5xl">{config.emoji}</span>
            <h3 className={`mb-2 text-lg font-bold ${colors.text}`}>Pr&oacute;ximamente</h3>
            <p className={`text-sm opacity-80 ${colors.text}`}>
              La descripci&oacute;n detallada de este nivel est&aacute; siendo preparada.
            </p>
          </div>
        ) : (
          <>
            {/* General Description */}
            <Card className={`${colors.border} ${colors.cardBg}`}>
              <CardHeader>
                <CardTitle className="text-lg">Descripci&oacute;n General</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-semibold leading-relaxed">{config.description}</p>
              </CardContent>
            </Card>

            {/* Sections Grid */}
            {config.sections.length > 0 && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {config.sections.map((section, i) => {
                  const sColors = SECTION_COLORS[section.color || "blue"]
                  return (
                    <Card key={i}>
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${sColors.bg}`}>
                            <span className={`text-sm ${sColors.text}`}>{section.icon || "\u2022"}</span>
                          </div>
                          <CardTitle className="text-lg">{section.title}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {typeof section.content === "string" ? (
                          <p className="text-muted-foreground text-sm leading-relaxed">{section.content}</p>
                        ) : (
                          <ul className="text-muted-foreground space-y-2 text-sm">
                            {section.content.map((item, j) => (
                              <li key={j}>&bull; {item}</li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            )}

            {/* Observable Behaviors */}
            {config.observableBehaviors && config.observableBehaviors.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${colors.bg}`}>
                      <IconCheck className={`h-5 w-5 ${colors.text}`} />
                    </div>
                    <CardTitle className="text-lg">Conductas Observables</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {config.observableBehaviors.map((behavior, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <IconCheck className={`mt-0.5 h-5 w-5 shrink-0 ${colors.text}`} />
                        <span className="text-muted-foreground text-sm">{behavior}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Deep Dive */}
            {config.deepDive && config.deepDive.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${colors.bg}`}>
                      <span className={"text-lg"}>📖</span>
                    </div>
                    <CardTitle className="text-lg">Profundizando</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-muted-foreground space-y-3 text-sm leading-relaxed">
                    {config.deepDive.map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Evolution */}
            {config.evolution && (
              <Card className={`${colors.border} ${colors.cardBg}`}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <IconArrowRight className={`h-6 w-6 ${colors.text}`} />
                    <CardTitle className="text-lg">{config.evolution.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div
                    className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${config.evolution.items.length <= 3 ? `lg:grid-cols-${config.evolution.items.length}` : ""}`}
                  >
                    {config.evolution.items.map((item, i) => (
                      <Card key={i}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">{item.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="text-muted-foreground space-y-1 text-sm">
                            {item.points.map((point, j) => (
                              <li key={j}>&bull; {point}</li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          {config.prev ? (
            <Button variant="outline" asChild>
              <a href={config.prev.url}>
                <IconArrowLeft className="mr-2 h-4 w-4" />
                {config.prev.name}
              </a>
            </Button>
          ) : (
            <div />
          )}
          {config.next ? (
            <Button asChild>
              <a href={config.next.url}>
                {config.next.name}
                <IconArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          ) : (
            <Button asChild>
              <a href="/growth/career-pathway">
                Volver a Career Pathway
                <IconArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
